import { useTranslation } from 'react-i18next';
import EmptyListResult from '../../components/EmptyListResult/EmptyListResult';
import { ActionIcon, Grid, Skeleton, Tooltip } from '@mantine/core';
import { useHistory } from 'react-router-dom';
import {
  IconBrandWhatsapp,
  IconCircleDashedCheck,
  IconEdit,
  IconPrinter,
  IconReceiptDollar,
  IconShirt,
  IconTrash
} from '@tabler/icons-react';
import { useState } from 'react';
import CatalogCard from '../../components/CatalogCard/CatalogCard';
import moment from 'moment';
import InfiniteScroll from 'react-infinite-scroll-component';
import { formatMoneyString, getCustomerFullName, getWhatsappURLString } from '../../utils/strings';
import { IOrder } from '../../interfaces/Order';

import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { IOrderProduct } from '../../interfaces/Product';
import OrdersCardMoreActionsMenu from '../OrdersCardMoreActionsMenu';
import PrintTicketDrawer from '../PrintTicketDrawer';
import {
  buildOrderFolio,
  getOrderCardColorsByAmountDueAndStatus,
  normalizeOrderScheduleTimeValue
} from '../../utils/ordersUtils';
import { getOrderAmountDue } from '@washday/utilities';
import { IStore } from '../../interfaces/Store';
import { getPiecesBreakdown, formatPiecesBreakdown } from '../../utils/helpers/getAllPieces';
import { useDisclosure } from '@mantine/hooks';
import useCanEditOrders from '../../hooks/useCanEditOrders';

const RequestedOrderCards = ({
  searchParamStoreId,
  onOrderAccepted,
  onOrderCancelled,
  onSendEmailReceipt,
  onDownloadPNGTicket,
  showOrderProductsModal,
  orders = [],
  fetchNextPage = () => {},
  hasNextPage = false
}: {
  orders: any;
  fetchNextPage: any;
  hasNextPage: boolean;
  searchParamStoreId: string;
  onOrderAccepted: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
  onOrderCancelled: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
  onSendEmailReceipt: (orderId: string) => Promise<void>;
  onPrintTicket: (order: IOrder) => Promise<void>;
  onDownloadPNGTicket: (order: IOrder) => Promise<void>;
  showOrderProductsModal: (order: IOrder) => void;
}) => {
  const { t } = useTranslation([
    'FieldName',
    'CommonButtons',
    'Notifications',
    'OrdersPage',
    'CleaningPage',
    'UpdateOrderForm',
    'ReportsPage',
    'OrdersHeaderSummary'
  ]);
  const history = useHistory();
  const canEditOrders = useCanEditOrders();
  const [openedPrintTicketDrawer, { open: openPrintTicketDrawer, close: closePrintTicketDrawer }] =
    useDisclosure(false);
  const [printTicketOrder, setPrintTicketOrder] = useState<IOrder | null>(null);

  const handleOpenPrintTicketDrawer = (order: IOrder) => {
    setPrintTicketOrder(order);
    openPrintTicketDrawer();
  };

  const handleClosePrintTicketDrawer = () => {
    closePrintTicketDrawer();
    setPrintTicketOrder(null);
  };

  return (
    <>
      <PrintTicketDrawer
        opened={openedPrintTicketDrawer}
        onClose={handleClosePrintTicketDrawer}
        order={printTicketOrder}
      />
      {orders && orders.length === 0 && <EmptyListResult />}
      <InfiniteScroll
        dataLength={orders?.length || 0}
        next={fetchNextPage}
        hasMore={hasNextPage}
        style={{ overflow: 'hidden', position: 'relative' }}
        loader={
          <>
            <Skeleton height={10} my={10} radius="md" />
            <Skeleton height={10} my={10} radius="md" />
            <Skeleton height={10} my={10} radius="md" />
          </>
        }
        // endMessage={<Empty description={false} />}
      >
        <>
          <Grid gutter="md" pos={'relative'}>
            {orders?.map((order: IOrder) => {
              const orderProducts = order?.products || [];
              const orderBuyAndGerProducts = order?.buyAndGetProducts || [];
              const allPieces = formatPiecesBreakdown(getPiecesBreakdown(orderProducts, orderBuyAndGerProducts), {
                measurementUnits: (order.createdIn as IStore)?.measurementUnits
              });
              const pickupDateLabel = order.pickup && order.pickupInfo?.date
                ? (() => {
                    const pickupDate = moment(order.pickupInfo?.date).format('DD/MM/YYYY');
                    const pickupFromTime = order.pickupInfo?.fromTime
                      ? normalizeOrderScheduleTimeValue(order.pickupInfo.fromTime)
                      : '';
                    const pickupToTime = order.pickupInfo?.toTime
                      ? normalizeOrderScheduleTimeValue(order.pickupInfo.toTime)
                      : '';

                    return [pickupDate, [pickupFromTime, pickupToTime].filter(Boolean).join(' - ')]
                      .filter(Boolean)
                      .join(' ');
                  })()
                : '';
              const deliveryDateLabel = order.deliveryInfo?.date
                ? moment(order.deliveryInfo.date).format('DD/MM/YYYY')
                : '';
              const amountDue = getOrderAmountDue(order);
              const customColors = getOrderCardColorsByAmountDueAndStatus({
                amountDue,
                highlightOrderCardsByPaymentPending: (order.createdIn as Partial<IStore>)
                  ?.ordersDisplayConfig?.highlightOrderCardsByPaymentPending,
                status: order.status
              });

              return (
                <Grid.Col key={order._id} span={{ xs: 12, sm: 6, lg: 4 }}>
                  <CatalogCard
                    fields={[
                      {
                        label: t('FieldName:pieces'),
                        value: allPieces
                      },
                      {
                        label: t('FieldName:name'),
                        value: getCustomerFullName(order.customer.name, order.customer.lastName)
                      },
                      {
                        label: t('FieldName:phone'),
                        value: order.customer.phone
                      },
                      {
                        label: t('FieldName:address'),
                        value: order.address?.formattedAddress
                      },
                      {
                        label: t('FieldName:total'),
                        value: formatMoneyString((order.total || 0).toFixed(2))
                      },
                      {
                        label: t('FieldName:createdDate'),
                        value: moment(order.createdDate).format('DD/MM/YYYY HH:mm') || ''
                      },
                      ...(pickupDateLabel
                        ? [
                            {
                              label: t('FieldName:pickupDate'),
                              value: pickupDateLabel
                            }
                          ]
                        : []),
                      ...(deliveryDateLabel
                        ? [
                            {
                              label: t('FieldName:deliveryDate'),
                              value: deliveryDateLabel
                            }
                          ]
                        : []),
                      {
                        label: t('FieldName:notes'),
                        value: order.notes || '',
                        valueTooltip: order.notes
                        // lineClamp: 5
                      }
                    ]}
                    cardActionButtons={[
                      {
                        action: () => showOrderProductsModal(order),
                        disabled: false,
                        Icon: <IconShirt />,
                        tooltip: t('OrdersPage:productsModal.tooltip')
                      },
                      {
                        action: () =>
                          history.push(`/orders/${order._id}?storeId=${searchParamStoreId}`, {
                            storeID: searchParamStoreId
                          }),
                        disabled: !canEditOrders,
                        Icon: <IconEdit />,
                        tooltip: t('CommonButtons:edit')
                      },
                      {
                        action: () =>
                          ConfirmationModal({
                            title: t('Notifications:CRUD.acceptedConfirmation'),
                            onConfirm: () =>
                              onOrderAccepted({
                                sequence: order.sequence || '',
                                storeId: order?.createdIn?._id || ''
                              }),
                            onCancel: () => {}
                          }),
                        disabled: false,
                        Icon: <IconCircleDashedCheck />,
                        tooltip: 'Aceptar Pedido'
                      },
                      {
                        action: () =>
                          ConfirmationModal({
                            title: t('Notifications:CRUD.cancelConfirmation'),
                            onConfirm: () =>
                              onOrderCancelled({
                                sequence: order.sequence || '',
                                storeId: order.createdIn?._id || ''
                              }),
                            onCancel: () => {}
                          }),
                        disabled: false,
                        Icon: <IconTrash />,
                        tooltip: 'Cancelar Pedido'
                      }
                    ]}
                    cardTitle={buildOrderFolio(order?.folioPrefix, order?.sequence) || ''}
                    cardTitleExtra={
                      <>
                        <Tooltip withinPortal={false} label="Imprimir Ticket">
                          <ActionIcon
                            color={customColors.headerTextColor}
                            onClick={() => handleOpenPrintTicketDrawer(order)}
                            variant="transparent"
                            aria-label="Imprimir Ticket">
                            <IconPrinter />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip
                          withinPortal={false}
                          label={
                            order.customer?.phone
                              ? t('CommonButtons:sendWhatsappMessage')
                              : t('CommonButtons:noCustomerPhone')
                          }>
                          <ActionIcon
                            color={customColors.headerTextColor || 'green'}
                            disabled={!order.customer.phone}
                            onClick={() => {
                              window.open(
                                getWhatsappURLString(
                                  order.customer?.phone,
                                  order.customer?.phoneCountryCode
                                ),
                                '_blank'
                              );
                            }}
                            variant="transparent"
                            aria-label={
                              order.customer?.phone
                                ? t('CommonButtons:sendWhatsappMessage')
                                : t('CommonButtons:noCustomerPhone')
                            }>
                            <IconBrandWhatsapp />
                          </ActionIcon>
                        </Tooltip>
                        <OrdersCardMoreActionsMenu order={order} customColors={customColors} />
                      </>
                    }
                    TitleIcon={IconReceiptDollar}
                    customColors={customColors}
                  />
                </Grid.Col>
              );
            })}
          </Grid>
        </>
      </InfiniteScroll>
    </>
  );
};

export default RequestedOrderCards;
